import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";

type VolunteerSettings = {
  globalRequiredHours: number;
  staffParticipationEnabled: boolean;
  requireApprovalByDefault: boolean;
  adminRecapEnabled: boolean;
  adminRecapRecipientEmails: string[];
  schoolYear: string;
};

function num(v: any, fallback: number) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function functionsClient() {
  return getFunctions(getApp(), "us-central1");
}

function previousMonthKey(now = new Date()) {
  const dt = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

function normalizeEmailList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[\n,;]+/g)
        .map((item) => item.trim().toLowerCase())
        .filter((item) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item))
    )
  );
}

export function VolunteerSettingsPage() {
  const { user, isAdmin, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [recapMessage, setRecapMessage] = useState<string | null>(null);
  const [adminRecapRecipientText, setAdminRecapRecipientText] = useState("zcatech@zionclassical.org");

  const [settings, setSettings] = useState<VolunteerSettings>({
    globalRequiredHours: 20,
    staffParticipationEnabled: false,
    requireApprovalByDefault: false,
    adminRecapEnabled: true,
    adminRecapRecipientEmails: ["zcatech@zionclassical.org"],
    schoolYear: "",
  });

  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      setLoading(true);
      setErr(null);

      try {
        const fn = httpsCallable<Record<string, never>, { ok: true; settings: VolunteerSettings }>(
          functionsClient(),
          "getVolunteerSettings"
        );
        const res = await fn({});
        setSettings(res.data.settings);
        setAdminRecapRecipientText((res.data.settings.adminRecapRecipientEmails || []).join("\n"));
      } catch (e: any) {
        console.error("[VolunteerSettingsPage] load failed", e);
        setErr(e?.message || "Failed to load volunteer settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  async function save() {
    if (!isAdmin) {
      setErr("Admin access is required.");
      return;
    }

    setSaving(true);
    setErr(null);

    try {
      const fn = httpsCallable<
        {
          globalRequiredHours: number;
          requireApprovalByDefault: boolean;
          staffParticipationEnabled?: boolean;
          adminRecapEnabled: boolean;
          adminRecapRecipientEmails: string[];
        },
        {
          ok: true;
          settings: {
            globalRequiredHours: number;
            requireApprovalByDefault: boolean;
            staffParticipationEnabled: boolean;
            adminRecapEnabled: boolean;
            adminRecapRecipientEmails: string[];
          };
        }
      >(functionsClient(), "upsertVolunteerSettings");

      const payload: {
        globalRequiredHours: number;
        requireApprovalByDefault: boolean;
        staffParticipationEnabled?: boolean;
        adminRecapEnabled: boolean;
        adminRecapRecipientEmails: string[];
      } = {
        globalRequiredHours: Math.max(0, num(settings.globalRequiredHours, 20)),
        requireApprovalByDefault: settings.requireApprovalByDefault === true,
        adminRecapEnabled: settings.adminRecapEnabled === true,
        adminRecapRecipientEmails: normalizeEmailList(adminRecapRecipientText),
      };

      if (isSuperAdmin) {
        payload.staffParticipationEnabled = settings.staffParticipationEnabled === true;
      }

      const res = await fn(payload);

      setSettings((prev) => ({
        ...prev,
        globalRequiredHours: res.data.settings.globalRequiredHours,
        requireApprovalByDefault: res.data.settings.requireApprovalByDefault,
        staffParticipationEnabled: res.data.settings.staffParticipationEnabled,
        adminRecapEnabled: res.data.settings.adminRecapEnabled,
        adminRecapRecipientEmails: res.data.settings.adminRecapRecipientEmails,
      }));
      setAdminRecapRecipientText((res.data.settings.adminRecapRecipientEmails || []).join("\n"));

      alert("Saved volunteer settings.");
    } catch (e: any) {
      console.error("[VolunteerSettingsPage] save failed", e);
      setErr(e?.message || "Failed to save volunteer settings.");
    } finally {
      setSaving(false);
    }
  }

  async function sendRecapNow() {
    if (!isAdmin) {
      setErr("Admin access is required.");
      return;
    }

    setSendingRecap(true);
    setErr(null);
    setRecapMessage(null);

    try {
      const fn = httpsCallable<
        { schoolYear?: string; statementMonth?: string; recipientEmails?: string[]; force?: boolean },
        { ok: true; skipped?: boolean; reason?: string; recipients?: string[]; summary?: { monthlyEntryCount?: number; monthlyApprovedHours?: number } }
      >(functionsClient(), "sendVolunteerAdminRecapNow");
      const res = await fn({
        schoolYear: settings.schoolYear || undefined,
        statementMonth: previousMonthKey(),
        recipientEmails: normalizeEmailList(adminRecapRecipientText),
        force: true,
      });

      if (res.data.skipped) {
        setRecapMessage(`Recap skipped: ${res.data.reason || "No recap was sent."}`);
      } else {
        setRecapMessage(`Recap sent to ${(res.data.recipients || []).join(", ")}.`);
      }
    } catch (e: any) {
      console.error("[VolunteerSettingsPage] send recap failed", e);
      setErr(e?.message || "Failed to send volunteer admin recap.");
    } finally {
      setSendingRecap(false);
    }
  }

  if (!user) {
    return (
      <div className="card">
        <h2>Volunteer Settings</h2>
        <div className="muted">Sign in.</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="card">
        <h2>Volunteer Settings</h2>
        <div className="muted">Admin only.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>Volunteer Settings</h2>
        <div className="muted">
          Phase 1 foundation for Volunteer Rebuild v2.
        </div>
        {loading ? <div className="muted" style={{ marginTop: 10 }}>Loading…</div> : null}
        {err ? (
          <div style={{ marginTop: 10, color: "#dc2626", whiteSpace: "pre-wrap" }}>
            {err}
          </div>
        ) : null}
      </div>

      <div className="card">
        <h3>Active School Year</h3>
        <div className="muted">
          Volunteer settings apply to the active school year.
        </div>

        <div style={{ marginTop: 12, maxWidth: 520 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            School Year
            <input className="input" value={settings.schoolYear || ""} readOnly />
          </label>
        </div>
      </div>

      <div className="card">
        <h3>Goal Policy</h3>
        <div className="muted">
          Sets the default required volunteer hours per family. Family-level overrides will be added next.
        </div>

        <div style={{ display: "grid", gap: 10, marginTop: 12, maxWidth: 520 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Global required family hours
            <input
              className="input"
              type="number"
              min={0}
              step="0.25"
              value={settings.globalRequiredHours}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  globalRequiredHours: Math.max(0, num(e.target.value, settings.globalRequiredHours)),
                })
              }
            />
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={settings.requireApprovalByDefault}
              onChange={(e) =>
                setSettings({ ...settings, requireApprovalByDefault: e.target.checked })
              }
            />
            <span>Require approval by default for family-authenticated submissions</span>
          </label>
        </div>
      </div>

      <div className="card">
        <h3>Staff Participation</h3>
        <div className="muted">
          When disabled, the Volunteer tab is hidden for staff-level accounts in the app.
          Only Super Admin may change this setting.
        </div>

        <div style={{ display: "grid", gap: 10, marginTop: 12, maxWidth: 720 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: isSuperAdmin ? 1 : 0.65 }}>
            <input
              type="checkbox"
              checked={settings.staffParticipationEnabled}
              disabled={!isSuperAdmin}
              onChange={(e) =>
                setSettings({ ...settings, staffParticipationEnabled: e.target.checked })
              }
            />
            <span>Enable staff participation in volunteer tracking</span>
          </label>

          {!isSuperAdmin ? (
            <div className="muted">
              You can view this setting, but only a Super Admin can change it.
            </div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <h3>Monthly Admin Recap</h3>
        <div className="muted">
          Sends an internal volunteer summary after the monthly family statements run.
        </div>

        <div style={{ display: "grid", gap: 12, marginTop: 12, maxWidth: 760 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={settings.adminRecapEnabled}
              onChange={(e) => setSettings({ ...settings, adminRecapEnabled: e.target.checked })}
            />
            <span>Send monthly admin recap report</span>
          </label>

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Admin recap recipients
            <textarea
              className="input"
              rows={5}
              value={adminRecapRecipientText}
              onChange={(e) => setAdminRecapRecipientText(e.target.value)}
              placeholder="zcatech@zionclassical.org"
            />
            <span className="muted">One email per line. Commas and semicolons also work.</span>
          </label>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn secondary" onClick={sendRecapNow} disabled={loading || saving || sendingRecap}>
              {sendingRecap ? "Sending Recap..." : `Send ${previousMonthKey()} Recap Now`}
            </button>
            {recapMessage ? <span className="muted">{recapMessage}</span> : null}
          </div>
        </div>
      </div>

      <div className="card">
        <h3>What&apos;s in this phase</h3>
        <div className="muted" style={{ display: "grid", gap: 8 }}>
          <div>• Global volunteer goal</div>
          <div>• Staff volunteer-tab gating</div>
          <div>• Family-authenticated submission foundation</div>
          <div>• Public family-code backend submission foundation</div>
          <div>• School-year-aware family dashboard foundation</div>
        </div>
      </div>

      <div className="card">
        <button className="btn" onClick={save} disabled={loading || saving}>
          {saving ? "Saving…" : "Save Volunteer Settings"}
        </button>
      </div>
    </div>
  );
}
